import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { useToast } from "@/hooks/use-toast";
import { Upload, X, Plus, Video, Loader2 } from "lucide-react";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/hooks/useAuth";
import { PageHeader } from "@/components/shared/PageHeader";

const categories = [
  "Electronics",
  "Fashion & Clothing",
  "Home & Garden",
  "Beauty & Health",
  "Sports & Outdoors",
  "Toys & Games",
  "Books & Media",
  "Automotive",
  "Food & Beverages",
  "Office Supplies",
  "Jewelry & Accessories",
  "Pet Supplies",
];

export default function AddProduct() {
  const navigate = useNavigate();
  const { toast } = useToast();
  const { user, loading: authLoading } = useAuth();
  const [loading, setLoading] = useState(false);
  const [images, setImages] = useState<string[]>([]);
  const [videoFile, setVideoFile] = useState<File | null>(null);
  const [videoPreview, setVideoPreview] = useState<string | null>(null);
  const [uploadingVideo, setUploadingVideo] = useState(false);
  const [formData, setFormData] = useState({
    title: "",
    description: "",
    price: "",
    category: "",
    stock: "",
    status: "active" as "active" | "draft",
  });
  const [selectedSizes, setSelectedSizes] = useState<string[]>([]);
  const [variations, setVariations] = useState<Array<{ name: string; value: string }>>([]);
  const [sizeType, setSizeType] = useState<'clothing' | 'shoes'>('clothing');

  const clothingSizes = ["XS", "S", "M", "L", "XL", "XXL", "2XL", "3XL"];
  const shoeSizes = ["36", "37", "38", "39", "40", "41", "42", "43", "44", "45", "46", "47", "48"];
  
  const isFashionCategory = formData.category === "Fashion & Clothing" || formData.category === "Jewelry & Accessories";
  const availableSizes = sizeType === 'shoes' ? shoeSizes : clothingSizes;

  const toggleSize = (size: string) => {
    setSelectedSizes(prev => 
      prev.includes(size) 
        ? prev.filter(s => s !== size)
        : [...prev, size]
    );
  };
  
  // Reset sizes when size type changes
  const handleSizeTypeChange = (type: 'clothing' | 'shoes') => {
    setSizeType(type);
    setSelectedSizes([]);
  };

  const addVariation = () => {
    setVariations(prev => [...prev, { name: "", value: "" }]);
  };

  const updateVariation = (index: number, field: "name" | "value", value: string) => {
    setVariations(prev => prev.map((v, i) => 
      i === index ? { ...v, [field]: value } : v
    ));
  };

  const removeVariation = (index: number) => {
    setVariations(prev => prev.filter((_, i) => i !== index));
  };

  // Check authentication
  useEffect(() => {
    if (!authLoading && !user) {
      toast({
        title: "Authentication Required",
        description: "Please login to add products",
        variant: "destructive",
      });
      navigate("/auth");
    }
  }, [authLoading, user, navigate, toast]);

  const handleImageUpload = (e: React.ChangeEvent<HTMLInputElement>) => {
    const files = e.target.files;
    if (!files) return;

    Array.from(files).forEach((file) => {
      const reader = new FileReader();
      reader.onloadend = () => {
        setImages((prev) => [...prev, reader.result as string].slice(0, 5)); // Max 5 images
      };
      reader.readAsDataURL(file);
    });
  };

  const removeImage = (index: number) => {
    setImages((prev) => prev.filter((_, i) => i !== index));
  };

  const handleVideoUpload = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;

    // Check if file is a video
    if (!file.type.startsWith('video/')) {
      toast({
        title: "Invalid File",
        description: "Please upload a video file",
        variant: "destructive",
      });
      return;
    }

    // Check file size (100MB max for videos)
    if (file.size > 100 * 1024 * 1024) {
      toast({
        title: "File Too Large",
        description: "Video must be less than 100MB",
        variant: "destructive",
      });
      return;
    }

    // Create video element to check duration
    const video = document.createElement('video');
    video.preload = 'metadata';
    
    video.onloadedmetadata = function() {
      window.URL.revokeObjectURL(video.src);
      
      if (video.duration > 60) {
        toast({
          title: "Video Too Long",
          description: "Video must be 1 minute or less",
          variant: "destructive",
        });
        return;
      }
      
      // Video is valid, set it
      setVideoFile(file);
      setVideoPreview(URL.createObjectURL(file));
    };
    
    video.src = URL.createObjectURL(file);
  };

  const removeVideo = () => {
    setVideoFile(null);
    setVideoPreview(null);
  };

  const uploadVideo = async (): Promise<string | null> => {
    if (!videoFile || !user) return null;

    try {
      setUploadingVideo(true);
      
      const fileExt = videoFile.name.split('.').pop();
      const fileName = `${user.id}/${Date.now()}.${fileExt}`;
      
      const { data, error } = await supabase.storage
        .from('product-videos')
        .upload(fileName, videoFile);

      if (error) throw error;

      const { data: urlData } = supabase.storage
        .from('product-videos')
        .getPublicUrl(fileName);

      return urlData.publicUrl;
    } catch (error) {
      console.error('Error uploading video:', error);
      toast({
        title: "Upload Failed",
        description: "Failed to upload video. Please try again.",
        variant: "destructive",
      });
      return null;
    } finally {
      setUploadingVideo(false);
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (images.length === 0) {
      toast({
        title: "Image Required",
        description: "Please upload at least one product image",
        variant: "destructive",
      });
      return;
    }

    if (!user) {
      toast({
        title: "Not Authenticated",
        description: "Please login to add products",
        variant: "destructive",
      });
      return;
    }

    setLoading(true);
    
    try {
      // Upload video if present
      let videoUrl = null;
      if (videoFile) {
        videoUrl = await uploadVideo();
      }

      // Upload images to storage
      const uploadedImages = [];
      for (const image of images) {
        const base64Data = image.split(',')[1];
        const byteCharacters = atob(base64Data);
        const byteNumbers = new Array(byteCharacters.length);
        for (let i = 0; i < byteCharacters.length; i++) {
          byteNumbers[i] = byteCharacters.charCodeAt(i);
        }
        const byteArray = new Uint8Array(byteNumbers);
        const blob = new Blob([byteArray], { type: 'image/jpeg' });
        
        const fileName = `${user.id}/${Date.now()}-${Math.random()}.jpg`;
        const { data, error } = await supabase.storage
          .from('products')
          .upload(fileName, blob);

        if (!error && data) {
          const { data: urlData } = supabase.storage
            .from('products')
            .getPublicUrl(fileName);
          uploadedImages.push(urlData.publicUrl);
        }
      }

      // Create product in database
      const { error } = await supabase
        .from('products')
        .insert({
          seller_id: user.id,
          title: formData.title,
          description: formData.description,
          price: parseFloat(formData.price),
          category: formData.category,
          stock: parseInt(formData.stock),
          status: formData.status,
          images: uploadedImages,
          video_url: videoUrl,
          sizes: selectedSizes,
          variations: variations.filter(v => v.name && v.value),
        });

      if (error) throw error;

      toast({
        title: "Product Added Successfully!",
        description: `${formData.title} has been added to your store`,
      });
      navigate("/seller/dashboard");
    } catch (error) {
      console.error('Error adding product:', error);
      toast({
        title: "Error",
        description: "Failed to add product. Please try again.",
        variant: "destructive",
      });
    } finally {
      setLoading(false);
    }
  };

  // Show loading state while checking auth
  if (authLoading) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <div className="text-center">
          <Loader2 className="h-8 w-8 animate-spin mx-auto mb-4" />
          <p className="text-muted-foreground">Loading...</p>
        </div>
      </div>
    );
  }

  // Don't render if no user (will redirect via useEffect)
  if (!user) {
    return null;
  }

  return (
    <div className="min-h-screen bg-background">
      <div className="container mx-auto px-4 py-8 max-w-4xl">
        <PageHeader
          title="Add New Product"
          description="Fill in the details below to add a new product to your store"
          backTo="/seller/dashboard"
        />

        <Card>
          <CardContent className="pt-6">
            <form onSubmit={handleSubmit} className="space-y-6">
              {/* Product Images */}
              <div className="space-y-2">
                <Label>Product Images *</Label>
                <div className="grid grid-cols-2 md:grid-cols-5 gap-4">
                  {images.map((image, index) => (
                    <div key={index} className="relative aspect-square">
                      <img
                        src={image}
                        alt={`Product ${index + 1}`}
                        className="w-full h-full object-cover rounded-lg"
                      />
                      <Button
                        type="button"
                        variant="destructive"
                        size="icon"
                        className="absolute top-2 right-2 h-6 w-6"
                        onClick={() => removeImage(index)}
                      >
                        <X className="h-3 w-3" />
                      </Button>
                    </div>
                  ))}
                  {images.length < 5 && (
                    <label className="aspect-square border-2 border-dashed border-muted-foreground/25 rounded-lg flex flex-col items-center justify-center cursor-pointer hover:border-primary transition-colors">
                      <Upload className="h-8 w-8 text-muted-foreground mb-2" />
                      <span className="text-xs text-muted-foreground">Upload</span>
                      <input
                        type="file"
                        accept="image/*"
                        multiple
                        onChange={handleImageUpload}
                        className="hidden"
                      />
                    </label>
                  )}
                </div>
                <p className="text-sm text-muted-foreground">
                  Upload up to 5 images. First image will be the main product image.
                </p>
              </div>

              {/* Product Video */}
              <div className="space-y-2">
                <Label>Product Video (Optional)</Label>
                {videoPreview ? (
                  <div className="relative">
                    <video
                      src={videoPreview}
                      controls
                      className="w-full max-w-2xl rounded-lg"
                    />
                    <Button
                      type="button"
                      variant="destructive"
                      size="icon"
                      className="absolute top-2 right-2"
                      onClick={removeVideo}
                    >
                      <X className="h-4 w-4" />
                    </Button>
                  </div>
                ) : (
                  <label className="border-2 border-dashed border-muted-foreground/25 rounded-lg p-8 flex flex-col items-center justify-center cursor-pointer hover:border-primary transition-colors">
                    <Video className="h-12 w-12 text-muted-foreground mb-3" />
                    <span className="text-sm font-medium mb-1">Upload Product Video</span>
                    <span className="text-xs text-muted-foreground">MP4, WebM, or OGG (Max 1 minute, 100MB)</span>
                    <input
                      type="file"
                      accept="video/*"
                      onChange={handleVideoUpload}
                      className="hidden"
                      disabled={uploadingVideo}
                    />
                  </label>
                )}
                {uploadingVideo && (
                  <div className="flex items-center gap-2 text-sm text-muted-foreground">
                    <div className="h-4 w-4 border-2 border-current border-t-transparent rounded-full animate-spin" />
                    Uploading video...
                  </div>
                )}
              </div>

              {/* Product Title */}
              <div className="space-y-2">
                <Label htmlFor="title">Product Title *</Label>
                <Input
                  id="title"
                  value={formData.title}
                  onChange={(e) => setFormData({ ...formData, title: e.target.value })}
                  placeholder="e.g., Wireless Bluetooth Headphones"
                  required
                />
              </div>

              {/* Description */}
              <div className="space-y-2">
                <Label htmlFor="description">Description *</Label>
                <Textarea
                  id="description"
                  value={formData.description}
                  onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                  placeholder="Describe your product in detail..."
                  rows={5}
                  required
                />
              </div>

              {/* Category */}
              <div className="space-y-2">
                <Label htmlFor="category">Category *</Label>
                <Select
                  value={formData.category}
                  onValueChange={(value) => setFormData({ ...formData, category: value })}
                  required
                >
                  <SelectTrigger>
                    <SelectValue placeholder="Select a category" />
                  </SelectTrigger>
                  <SelectContent>
                    {categories.map((category) => (
                      <SelectItem key={category} value={category}>
                        {category}
                      </SelectItem>
                    ))}
              </SelectContent>
                </Select>
              </div>

              {/* Product Sizes */}
              <div className="space-y-2">
                <Label>Available Sizes (Optional)</Label>
                
                {/* Size Type Toggle for Fashion Categories */}
                {isFashionCategory && (
                  <div className="flex gap-2 mb-3">
                    <button
                      type="button"
                      onClick={() => handleSizeTypeChange('clothing')}
                      className={`px-4 py-2 rounded-md border text-sm font-medium transition-colors ${
                        sizeType === 'clothing'
                          ? "bg-primary text-primary-foreground border-primary"
                          : "bg-background border-input hover:bg-accent hover:text-accent-foreground"
                      }`}
                    >
                      Clothing Sizes
                    </button>
                    <button
                      type="button"
                      onClick={() => handleSizeTypeChange('shoes')}
                      className={`px-4 py-2 rounded-md border text-sm font-medium transition-colors ${
                        sizeType === 'shoes'
                          ? "bg-primary text-primary-foreground border-primary"
                          : "bg-background border-input hover:bg-accent hover:text-accent-foreground"
                      }`}
                    >
                      Shoe Sizes (EU)
                    </button>
                  </div>
                )}
                
                <div className="flex flex-wrap gap-2">
                  {availableSizes.map((size) => (
                    <button
                      key={size}
                      type="button"
                      onClick={() => toggleSize(size)}
                      className={`px-4 py-2 rounded-md border text-sm font-medium transition-colors ${
                        selectedSizes.includes(size)
                          ? "bg-primary text-primary-foreground border-primary"
                          : "bg-background border-input hover:bg-accent hover:text-accent-foreground"
                      }`}
                    >
                      {size}
                    </button>
                  ))}
                </div>
                <p className="text-sm text-muted-foreground">
                  {isFashionCategory 
                    ? `Select ${sizeType === 'shoes' ? 'shoe' : 'clothing'} sizes available for this product`
                    : 'Select all sizes available for this product'
                  }
                </p>
              </div>

              {/* Product Variations */}
              <div className="space-y-2">
                <div className="flex items-center justify-between">
                  <Label>Product Variations (Optional)</Label>
                  <Button
                    type="button"
                    variant="outline"
                    size="sm"
                    onClick={addVariation}
                  >
                    <Plus className="h-4 w-4 mr-1" />
                    Add Variation
                  </Button>
                </div>
                <p className="text-sm text-muted-foreground">
                  Add variations like colors, materials, or styles
                </p>
                {variations.length > 0 && (
                  <div className="space-y-3 mt-3">
                    {variations.map((variation, index) => (
                      <div key={index} className="flex gap-3 items-start">
                        <div className="flex-1">
                          <Input
                            placeholder="Type (e.g., Color, Material)"
                            value={variation.name}
                            onChange={(e) => updateVariation(index, "name", e.target.value)}
                          />
                        </div>
                        <div className="flex-1">
                          <Input
                            placeholder="Value (e.g., Red, Cotton)"
                            value={variation.value}
                            onChange={(e) => updateVariation(index, "value", e.target.value)}
                          />
                        </div>
                        <Button
                          type="button"
                          variant="destructive"
                          size="icon"
                          onClick={() => removeVariation(index)}
                        >
                          <X className="h-4 w-4" />
                        </Button>
                      </div>
                    ))}
                  </div>
                )}
              </div>

              {/* Price and Stock */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label htmlFor="price">Price (₦) *</Label>
                  <Input
                    id="price"
                    type="number"
                    value={formData.price}
                    onChange={(e) => setFormData({ ...formData, price: e.target.value })}
                    placeholder="0.00"
                    min="0"
                    step="0.01"
                    required
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="stock">Stock Quantity *</Label>
                  <Input
                    id="stock"
                    type="number"
                    value={formData.stock}
                    onChange={(e) => setFormData({ ...formData, stock: e.target.value })}
                    placeholder="0"
                    min="0"
                    required
                  />
                </div>
              </div>

              {/* Product Status */}
              <div className="space-y-2">
                <Label htmlFor="status">Product Status</Label>
                <Select
                  value={formData.status}
                  onValueChange={(value: "active" | "draft") => 
                    setFormData({ ...formData, status: value })
                  }
                >
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="draft">Draft (Not visible to buyers)</SelectItem>
                    <SelectItem value="active">Active (Visible to buyers)</SelectItem>
                  </SelectContent>
                </Select>
              </div>

              {/* Action Buttons */}
              <div className="flex gap-4 pt-4">
                <Button
                  type="button"
                  variant="outline"
                  onClick={() => navigate("/seller/dashboard")}
                  className="flex-1"
                >
                  Cancel
                </Button>
                <Button
                  type="submit"
                  variant="gradient"
                  disabled={loading}
                  className="flex-1"
                >
                  {loading ? (
                    <>
                      <div className="h-4 w-4 border-2 border-current border-t-transparent rounded-full animate-spin mr-2" />
                      Adding Product...
                    </>
                  ) : (
                    <>
                      <Plus className="h-4 w-4 mr-2" />
                      Add Product
                    </>
                  )}
                </Button>
              </div>
            </form>
          </CardContent>
        </Card>
      </div>
    </div>
  );
}